'use client';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

import CardsSkeleton from '@/common/components/SalesFlow/StepsContent/components/common/CardsSkeleton/CardsSkeleton';
import { StepsContentProps } from '@/common/components/SalesFlow/StepsContent/StepContent.interfaces';
import { useScrollSection } from '@/common/components/SalesFlow/StepsContent/hooks/useScrollSection';
import { getStepId } from '@/common/components/SalesFlow/StepsContent/utils/getStepId';
import { useExtensionFlow } from '@/widgets/ExtensionFlow/context/ExtensionFlowContext';
import { Languages } from '@/common/utils/constants';
import AdditionalModules from '@/widgets/BecomeAMember/components/StepsContent/components/AdditionalModules/AdditionalModules';

import { FrequencyToggler } from '@/common/components/FrequencyToggler/FrequencyToggler';
import PaymentMethodSection from '@/common/components/SalesFlow/SalesFlowRegistrationForm/sections/PaymentMethodSection';

import ExtensionMembershipType from './components/ExtensionMembershipType/ExtensionMembershipType';
import ExtensionAccessLevel from './components/ExtensionAccessLevel/ExtensionAccessLevel';
import ExtensionTermsAndCondition from './components/ExtensionTermsAndCondition/ExtensionTermsAndCondition';
import {
  useExtensionPaymentFlow,
  useExtensionScrollStepContent,
  useExtensionStepContent,
} from './StepContent.service';
import { FlowType } from '@/common/api/interfaces';

const ExtensionTerms = dynamic(() => import('./components/ExtensionTerms/ExtensionTerms'), {
  ssr: false,
});

export default function ExtensionFlowStepsContent({
  studioId,
  stepsCollection,
  combinationsData,
  lang,
  fetchingCombinationComplete,
  isExtending,
  club
}: StepsContentProps) {
  const [paymentMethodError, setPaymentMethodError] = useState<string | null>(null);
  const paymentMethodSectionRef = useRef<HTMLFieldSetElement>(null);

  const {
    pushToDataLayer,
    setFreeModules,
    setPaidModules,
    studioTermData,
    isInvalidVoucher,
    translations,
    setActiveStep,
    isPaymentWidgetVisible,
    setIsPaymentWidgetVisible,
    allowedPaymentMethods
  } = useExtensionFlow();

  const {
    isPaymentSkipped,
    onRegisterValidator,
    handleWidgetSuccess,
    isProcessingSignUp,
    hasSavedPaymentInfo,
    isPaymentSessionFailed,
    isPaymentSessionLoading,
    validateTerms,
  } = useExtensionPaymentFlow({ club, lang });

  const {
    stepTitles,
    selectedType,
    rateBundleId,
    rateBundleTermId,
    selectedTerm,
    extrasSave,
    extraModules,
    termsData,
    isTermsLoading,
    hasAdditionalModules,
    membershipTypes,
    accessLevels,
    stepItems,
    stepsMap,
    availability,
    bundles,
    isTogglerAvailable
  } = useExtensionStepContent({
    studioId,
    stepsCollection,
    combinationsData,
    lang,
    isExtending,
  });

  const {
    typeRef,
    accessLevelRef,
    termsRef,
    extrasRef,
    paymentRef,
    sections,
    scrollToSavedStep,
    isManuallyScrolling
  } = useExtensionScrollStepContent({
    hasAdditionalModules,
    stepsCollection,
    isExtending,
    isPaymentWidgetVisible
  });

  useScrollSection(sections, stepItems, isManuallyScrolling, setActiveStep);

  useEffect(() => {
    if (!stepsCollection || isTermsLoading || !fetchingCombinationComplete) return;
    scrollToSavedStep();
  }, [
    selectedType,
    rateBundleId,
    selectedTerm,
    extrasSave,
    extraModules,
    hasAdditionalModules,
    isTermsLoading,
    stepsCollection,
    fetchingCombinationComplete,
    isPaymentWidgetVisible
  ]);

  const maximumSelectableModules =
    termsData?.maximumNumberOfSelectableModules ?? 0;

  const extensionTermsAndConditions = rateBundleId ? (
    <ExtensionTermsAndCondition
      club={club}
      isInvalidVoucher={isInvalidVoucher}
      translations={translations}
      isSubmitButtonHidden={!isPaymentSkipped}
      skipPayment={isPaymentSkipped}
      lang={lang}
      onRegisterValidator={onRegisterValidator}
    />
  ) : null;

  if (!membershipTypes || !accessLevels || !fetchingCombinationComplete) {
    return (
      <Box>
        <CardsSkeleton />
      </Box>
    );
  }

  return (
    <>
      <Box id={getStepId(stepsMap?.membershipType)} ref={typeRef}>
        <ExtensionMembershipType
          step={stepTitles.membershipType}
          data={membershipTypes}
          lang={lang}
          setIsPaymentWidgetVisible={setIsPaymentWidgetVisible}
        />
      </Box>
      {selectedType && (
        <Box id={getStepId(stepsMap.accessLevel)} ref={accessLevelRef}>
          <ExtensionAccessLevel
            step={stepTitles.accessLevel}
            data={accessLevels}
            lang={lang}
            fetchingCombinationComplete={fetchingCombinationComplete}
            translations={translations}
            setIsPaymentWidgetVisible={setIsPaymentWidgetVisible}
            isExtending={!!isExtending}
          />
        </Box>
      )}
      {rateBundleId && (
        <Box id={getStepId(stepsMap.terms)} ref={termsRef}>
          <ExtensionTerms
            isExtending={!!isExtending}
            data={termsData?.terms ?? []}
            selectableModules={termsData?.selectableModules ?? []}
            lang={lang}
            step={stepTitles.terms}
            isFetching={isTermsLoading}
            setIsPaymentWidgetVisible={setIsPaymentWidgetVisible}
            frequencyToggler={isTogglerAvailable ? (
              <FrequencyToggler
                availability={availability}
                bundles={bundles}
                translations={translations}
              />
            ) : null}
          />
        </Box>
      )}
      {hasAdditionalModules && extraModules && (
        <Box
          id={getStepId(stepsMap.extras)}
          ref={extrasRef}
        >
          <AdditionalModules
            isExtending={!!isExtending}
            maximumSelectableModules={maximumSelectableModules}
            freeModules={termsData?.selectableModules}
            paidModules={studioTermData?.optionalModules}
            setFreeModules={setFreeModules}
            setPaidModules={setPaidModules}
            step={stepTitles.modules}
            lang={lang}
            pushToDataLayer={pushToDataLayer}
            translations={translations}
          />
        </Box>
      )}
      {isPaymentSkipped && extensionTermsAndConditions}

      {rateBundleTermId && !isPaymentSkipped && (
        <Box id={getStepId(stepsMap.payment)} ref={paymentRef}>
          <PaymentMethodSection
            translations={translations}
            ref={paymentMethodSectionRef}
            error={paymentMethodError}
            setError={setPaymentMethodError}
            allowedPaymentMethods={allowedPaymentMethods}
            lang={lang as Languages}
            step={stepTitles.payment}
            isPaymentWidgetVisible={isPaymentWidgetVisible}
            onSuccess={handleWidgetSuccess}
            isProcessingSignUp={isProcessingSignUp}
            hasSavedPaymentInfo={hasSavedPaymentInfo}
            isPaymentSessionLoading={isPaymentSessionLoading}
            isPaymentSessionFailed={isPaymentSessionFailed}
            validateTerms={validateTerms}
            flowType={FlowType.EXTENSION}
          >
            {extensionTermsAndConditions}
          </PaymentMethodSection>
        </Box>
      )}
    </>
  );
}

